import i18n from '@/i18n';
/** 刊登模板编辑页：按分区标题拆分列范围与布局模式 */

/** Excel 列字母 → 0-based 列索引（A→0, C→2, AR→43, CA→78） */
export function excelColLetterToIndex(letters) {
  const s = String(letters || '').trim().toUpperCase();
  if (!s) return -1;
  let result = 0;
  for (let i = 0; i < s.length; i += 1) {
    const code = s.charCodeAt(i);
    if (code < 65 || code > 90) return -1;
    result = result * 26 + (code - 64);
  }
  return result - 1;
}

/** SHEIN 第 5、6 行说明合并为 tooltip 文本 */
export function mergeSheinTooltipText(tooltip) {
  if (!tooltip) return '';
  const parts = [tooltip.row5, tooltip.row6]
    .map((value) => String(value || '').trim())
    .filter(Boolean);
  return parts.join('\n');
}

/**
 * SHEIN 模板分区（可由官方规则覆盖）：
 * - 基础信息：C2~I2
 * - 商品属性：AR2~CA2
 * - 商品信息：其余有表头的列
 */
export function buildSheinListingSectionPlans(sheinHeaderRow, ruleSections) {
  const sections = Array.isArray(ruleSections) && ruleSections.length > 0
    ? ruleSections
    : [
      { category: '基础信息', colStart: 'C', colEnd: 'I' },
      { category: '商品属性', colStart: 'AR', colEnd: 'CA' },
      { category: '商品信息', remainder: true },
    ];

  const basicSection = sections.find((s) => s.category === '基础信息') || sections[0];
  const attrSection = sections.find((s) => s.category === '商品属性') || sections[1];
  const productSection = sections.find((s) => s.category === '商品信息') || sections[2];

  const basicStart = excelColLetterToIndex(basicSection?.colStart || 'C');
  const basicEnd = excelColLetterToIndex(basicSection?.colEnd || 'I');
  const attrStart = excelColLetterToIndex(attrSection?.colStart || 'AR');
  const attrEnd = excelColLetterToIndex(attrSection?.colEnd || 'CA');

  const basicIndices = [];
  const attrIndices = [];
  const productInfoIndices = [];

  (sheinHeaderRow || []).forEach((header, index) => {
    const headerText = String(header || '').trim();
    if (!headerText) return;

    if (index >= basicStart && index <= basicEnd) {
      basicIndices.push(index);
    } else if (index >= attrStart && index <= attrEnd) {
      attrIndices.push(index);
    } else if (productSection?.remainder !== false) {
      productInfoIndices.push(index);
    }
  });

  const plans = [];
  if (basicIndices.length > 0) {
    plans.push({
      title: '基础信息',
      layout: 'basic-info',
      dataColIndices: basicIndices,
      startDataCol: basicIndices[0],
      colCount: basicIndices.length,
    });
  }
  if (attrIndices.length > 0) {
    plans.push({
      title: '商品属性',
      layout: 'product-attributes',
      dataColIndices: attrIndices,
      startDataCol: attrIndices[0],
      colCount: attrIndices.length,
    });
  }
  if (productInfoIndices.length > 0) {
    plans.push({
      title: '商品信息',
      layout: 'sku-info',
      dataColIndices: productInfoIndices,
      startDataCol: productInfoIndices[0],
      colCount: productInfoIndices.length,
    });
  }
  return plans;
}

/**
 * 行级 SKU 字段（不应被「第 0 行通用属性」同步覆盖）。
 * 部分模板里这些列可能落在商品属性列范围内，复制行时会被误重置成第一行。
 */
export function isSheinPerSkuAttributeHeader(header) {
  const h = String(header || '').replace(/^\*/, '').trim();
  if (!h) return false;
  return /sku|货号|卖家\s*sku|supplier\s*sku|尺码|size|尺寸|颜色|color|colour|色号|库存|库存量|quantity|qty|价格|price|售价|供货价|重量|weight/i.test(h);
}

/**
 * SHEIN：基本信息 / 商品属性为通用属性，对应完整 headers 行上的列下标
 *（含前置「+」列，故 dataColIndex + 1）。
 * 明确排除 SKU/尺码/颜色等行级字段。
 */
export function getSheinSharedAttributeHeaderIndices(headers, ruleSections) {
  const dataHeaders = (headers || []).slice(1, -1);
  if (dataHeaders.length === 0) return [];
  const plans = buildSheinListingSectionPlans(dataHeaders, ruleSections);
  const indices = [];
  for (const plan of plans) {
    if (plan.layout !== 'basic-info' && plan.layout !== 'product-attributes') continue;
    const cols = Array.isArray(plan.dataColIndices) ? plan.dataColIndices : [];
    for (const dataColIndex of cols) {
      const headerIndex = dataColIndex + 1;
      if (headerIndex <= 0 || headerIndex >= (headers?.length || 0) - 1) continue;
      if (isSheinPerSkuAttributeHeader(headers[headerIndex])) continue;
      indices.push(headerIndex);
    }
  }
  return indices;
}

/**
 * 将第 0 行的基本信息 / 商品属性（通用属性）同步到每一行商品信息行。
 * 行数仍由商品信息列表决定，通用属性每行都要有相同值。
 */
export function propagateSheinSharedAttributeValues(headers, rows, ruleSections) {
  if (!Array.isArray(rows) || rows.length <= 1) return rows;
  const sharedIndices = getSheinSharedAttributeHeaderIndices(headers, ruleSections);
  if (sharedIndices.length === 0) return rows;
  const source = rows[0];
  if (!Array.isArray(source)) return rows;

  return rows.map((row, rowIndex) => {
    if (rowIndex === 0 || !Array.isArray(row)) return row;
    const next = [...row];
    for (const idx of sharedIndices) {
      next[idx] = source[idx];
    }
    return next;
  });
}

export function resolveListingSectionLayout(title) {
  const t = String(title || '').trim();
  if (/SKU图片/i.test(t)) return 'sku-image';
  if (/SKU信息/i.test(t)) return 'sku-info';
  if (/商品信息/i.test(t)) return 'sku-info';
  if (/商品详情|详情/i.test(t)) return 'product-detail';
  if (/商品属性/i.test(t)) return 'product-attributes';
  if (/基本信息|基础信息/i.test(t)) return 'basic-info';
  return 'fields';
}

/** 分区展示标题（对齐参考布局文案） */
export function getListingSectionDisplayTitle(title) {
  const t = String(title || '').trim();
  if (
    t === '基础信息'
    || t === '基本信息'
    || t === 'Basic info'
    || t === i18n.t('workbench:ipeUtilError.sectionBasicInfo')
  ) {
    return i18n.t('workbench:ipeUtilError.sectionBasicInfo');
  }
  if (/SPU商品属性|SPU attributes/i.test(t)) {
    return i18n.t('workbench:ipeUtilError.sectionSpuInfo');
  }
  if (
    t === '商品属性'
    || t === 'Product attributes'
    || t === i18n.t('workbench:ipeUtilError.sectionProductAttrs')
  ) {
    return i18n.t('workbench:ipeUtilError.sectionProductAttrs');
  }
  if (
    t === '商品信息'
    || t === 'Product info'
    || t === i18n.t('workbench:ipeUtilError.sectionProductInfo')
  ) {
    return i18n.t('workbench:ipeUtilError.sectionProductInfo');
  }
  return t;
}

/** TEMU 第 1/2 行：label 来自第 1 行，value 来自第 2 行 */
export function parseTemuGlobalInfoPairs(row1, row2) {
  const maxLen = Math.max(row1?.length || 0, row2?.length || 0);
  const items = [];
  for (let index = 0; index < maxLen; index += 1) {
    const label = String(row1?.[index] ?? '').trim();
    const value = String(row2?.[index] ?? '').trim();
    if (!label && !value) continue;
    items.push({ label: label || '—', value });
  }
  return items;
}

function isTemuRequiredTooltip(tooltip) {
  const text = String(tooltip || '').trim();
  return Boolean(
    text
    && text.includes('必填')
    && !text.includes('条件必填')
    && !text.includes('非必填'),
  );
}

/** 解析 TEMU 第 4/5 行指定列范围内的字段元数据 */
export function parseTemuColumnRange(row4, row5, startCol, endCol) {
  const headers = [];
  const tooltips = [];
  const requiredFlags = [];
  const colIndexMap = [];

  if (startCol < 0 || endCol < startCol) {
    return { headers, tooltips, requiredFlags, colIndexMap };
  }

  for (let col = startCol; col <= endCol; col += 1) {
    const header = String(row4?.[col] ?? '').trim();
    if (!header) continue;
    const tooltip = String(row5?.[col] ?? '').trim();
    headers.push(header);
    tooltips.push(tooltip);
    requiredFlags.push(isTemuRequiredTooltip(tooltip));
    colIndexMap.push(col);
  }

  return { headers, tooltips, requiredFlags, colIndexMap };
}

/**
 * TEMU 模板分区（可由官方规则覆盖）：
 * - 基础信息：A4~F4
 * - 商品属性：G4~BW4
 * - 商品信息：BX4~DW4
 */
export function buildTemuListingSectionPlans(row4, ruleSections) {
  const sections = Array.isArray(ruleSections) && ruleSections.length > 0
    ? ruleSections
    : [
      { category: '基础信息', colStart: 'A', colEnd: 'F' },
      { category: '商品属性', colStart: 'G', colEnd: 'BW' },
      { category: '商品信息', colStart: 'BX', colEnd: 'DW' },
    ];

  const basicSection = sections.find((s) => s.category === '基础信息') || sections[0];
  const attrSection = sections.find((s) => s.category === '商品属性') || sections[1];
  const productSection = sections.find((s) => s.category === '商品信息') || sections[2];

  const basicStart = excelColLetterToIndex(basicSection?.colStart || 'A');
  const basicEnd = excelColLetterToIndex(basicSection?.colEnd || 'F');
  const attrStart = excelColLetterToIndex(attrSection?.colStart || 'G');
  const attrEnd = excelColLetterToIndex(attrSection?.colEnd || 'BW');
  const productStart = excelColLetterToIndex(productSection?.colStart || 'BX');
  const productEnd = excelColLetterToIndex(productSection?.colEnd || 'DW');

  const basicIndices = [];
  const attrIndices = [];
  const productIndices = [];

  (row4 || []).forEach((header, index) => {
    const headerText = String(header || '').trim();
    if (!headerText) return;
    if (index >= basicStart && index <= basicEnd) basicIndices.push(index);
    else if (index >= attrStart && index <= attrEnd) attrIndices.push(index);
    else if (index >= productStart && index <= productEnd) productIndices.push(index);
  });

  const plans = [];
  if (basicIndices.length > 0) {
    plans.push({
      title: '基础信息',
      layout: 'basic-info',
      dataColIndices: basicIndices,
      startDataCol: basicIndices[0],
      colCount: basicIndices.length,
    });
  }
  if (attrIndices.length > 0) {
    plans.push({
      title: '商品属性',
      layout: 'product-attributes',
      dataColIndices: attrIndices,
      startDataCol: attrIndices[0],
      colCount: attrIndices.length,
    });
  }
  if (productIndices.length > 0) {
    plans.push({
      title: '商品信息',
      layout: 'sku-info',
      dataColIndices: productIndices,
      startDataCol: productIndices[0],
      colCount: productIndices.length,
    });
  }
  return plans;
}

export function buildTemuTableSectionPlans(sectionTitles, dataColCount) {
  if (!Number.isFinite(dataColCount) || dataColCount <= 0) return [];

  if (!Array.isArray(sectionTitles) || sectionTitles.length === 0) {
    return [{
      title: '商品规格',
      layout: 'fields',
      startDataCol: 0,
      colCount: dataColCount,
    }];
  }

  const plans = [];
  sectionTitles.forEach((sectionTitle, titleIndex) => {
    const nextTitleCol = titleIndex < sectionTitles.length - 1
      ? sectionTitles[titleIndex + 1].col
      : null;

    let colCount = 0;
    if (nextTitleCol !== null) {
      const actualStartCol = sectionTitle.col + 1;
      const actualEndCol = nextTitleCol + 1;
      colCount = Math.max(0, actualEndCol - actualStartCol);
    } else {
      let previousColCount = 0;
      for (let i = 0; i < titleIndex; i += 1) {
        const prevNextCol = i < sectionTitles.length - 1
          ? sectionTitles[i + 1].col
          : null;
        if (prevNextCol !== null) {
          const prevActualStartCol = sectionTitles[i].col + 1;
          const prevActualEndCol = prevNextCol + 1;
          previousColCount += Math.max(0, prevActualEndCol - prevActualStartCol);
        }
      }
      colCount = Math.max(0, dataColCount - previousColCount);
    }

    const startDataCol = plans.reduce((sum, item) => sum + item.colCount, 0);
    if (colCount <= 0) return;

    plans.push({
      title: sectionTitle.title,
      layout: resolveListingSectionLayout(sectionTitle.title),
      startDataCol,
      colCount,
    });
  });

  return plans;
}

/** SKU 信息区合并「商品规格 + SKU信息」列，与参考布局一致 */
export function buildTemuSkuDisplayPlans(plans) {
  if (!plans?.length) return [];

  const hasSkuInfo = plans.some((p) => /SKU信息/i.test(p.title));
  const specPlan = plans.find((p) => /商品规格/.test(p.title));

  return plans
    .filter((plan) => {
      if (hasSkuInfo && /商品规格/.test(plan.title)) return false;
      return true;
    })
    .map((plan) => {
      if (!hasSkuInfo || !/SKU信息/i.test(plan.title)) return plan;
      const infoPlan = plan;
      const start = specPlan ? specPlan.startDataCol : infoPlan.startDataCol;
      const end = infoPlan.startDataCol + infoPlan.colCount;
      return {
        ...infoPlan,
        title: 'SKU信息',
        layout: 'sku-info',
        startDataCol: start,
        colCount: Math.max(infoPlan.colCount, end - start),
      };
    });
}

export function buildFilteredTemuHeaders(allHeaders, startDataCol, colCount) {
  const dataHeaders = (allHeaders || []).slice(1, -1);
  const slice = dataHeaders.slice(startDataCol, startDataCol + colCount);
  return ['+', ...slice, '操作'];
}

export function getTemuSectionDataColIndices(startDataCol, colCount) {
  return Array.from({ length: colCount }, (_, index) => startDataCol + index);
}

export function getTemuRowVisibleCellIndices(row, startDataCol, colCount) {
  if (!Array.isArray(row) || row.length === 0) return [];
  const indices = [0];
  for (let i = 0; i < colCount; i += 1) {
    indices.push(startDataCol + 1 + i);
  }
  indices.push(row.length - 1);
  return indices.filter((value, index, arr) => arr.indexOf(value) === index);
}

export function findTemuColorDataColIndex(headers, startDataCol, colCount) {
  const dataHeaders = (headers || []).slice(1, -1);
  const slice = dataHeaders.slice(startDataCol, startDataCol + colCount);
  const localIdx = slice.findIndex((header) => /^(color|颜色|colour|色系)$/i.test(String(header || '').trim()));
  if (localIdx >= 0) return startDataCol + localIdx;

  const specPlan = { startDataCol, colCount };
  if (colCount > 0) return specPlan.startDataCol;
  return 0;
}

export function groupTemuRowsByDataColumn(data, dataColIndex) {
  const groups = [];
  const seen = new Map();

  (data || []).forEach((row, rowIndex) => {
    if (!Array.isArray(row)) return;
    const cellIndex = dataColIndex + 1;
    const raw = String(row[cellIndex] || '').trim();
    const label = raw || `规格 ${rowIndex + 1}`;
    const key = raw || `__row_${rowIndex}`;

    if (!seen.has(key)) {
      const group = { key, label, rowIndex, rowIndices: [rowIndex] };
      seen.set(key, group);
      groups.push(group);
    } else {
      seen.get(key).rowIndices.push(rowIndex);
    }
  });

  return groups;
}

/** 非 TEMU 平台：按表头名称推断分区 */
export function buildGenericListingSectionPlans(headers) {
  const dataHeaders = (headers || []).slice(1, -1);
  if (!dataHeaders.length) return [];

  const buckets = [
    { title: '基本信息', layout: 'basic-info', indices: [] },
    { title: '商品详情', layout: 'product-detail', indices: [] },
    { title: 'SKU图片', layout: 'sku-image', indices: [] },
    { title: 'SKU信息', layout: 'sku-info', indices: [] },
    { title: '其他字段', layout: 'fields', indices: [] },
  ];

  dataHeaders.forEach((header, index) => {
    const h = String(header || '').trim();
    if (/商品名称|product name|title/i.test(h)) {
      buckets[0].indices.push(index);
    } else if (/sku.*图|sku image|sku图|主图|轮播|image/i.test(h) && !/信息|inventory|price|库存|价格/i.test(h)) {
      buckets[2].indices.push(index);
    } else if (/color|size|颜色|尺码|价格|库存|sku|货号|seller|全球商品/i.test(h)) {
      buckets[3].indices.push(index);
    } else if (/图片|描述|image|description|detail|视频|text/i.test(h)) {
      buckets[1].indices.push(index);
    } else {
      buckets[4].indices.push(index);
    }
  });

  return buckets
    .filter((bucket) => bucket.indices.length > 0)
    .map((bucket) => ({
      title: bucket.title,
      layout: bucket.layout,
      startDataCol: bucket.indices[0],
      colCount: bucket.indices.length,
      dataColIndices: bucket.indices,
    }));
}

/** SHEIN 刊登模板：单元格（td）总宽 300px = 左右内边距 10+10 + 固定值 80 + input 200 */
export const SHEIN_LISTING_FIELD_COL_WIDTH = 300;
export const SHEIN_LISTING_FIELD_GAP = 0;
export const SHEIN_LISTING_VALUE_DROPDOWN_WIDTH = 80;
export const SHEIN_LISTING_INPUT_WIDTH = 200;
export const SHEIN_LISTING_CELL_PADDING_X = 10;
export const SHEIN_LISTING_MAX_COLUMNS_PER_ROW = 9;
export const SHEIN_LISTING_ADD_COL_WIDTH = 48;
export const SHEIN_LISTING_OPERATION_COL_WIDTH = 140;
/** 商品信息右侧固定操作列宽（含复制、复制多个、删除） */
export const SHEIN_LISTING_OPERATION_PINNED_COL_WIDTH = 220;

export function getSheinListingFieldColWidth(header) {
  if (header === '+') return SHEIN_LISTING_ADD_COL_WIDTH;
  if (header === '操作') return SHEIN_LISTING_OPERATION_COL_WIDTH;
  return SHEIN_LISTING_FIELD_COL_WIDTH;
}

/** 根据表头列表计算 SHEIN 表格总宽度（每列固定宽；「+」列编辑页隐藏不计入） */
export function getSheinListingTableWidthPx(headers) {
  if (!Array.isArray(headers) || headers.length === 0) return 0;
  return headers.reduce((sum, header) => {
    if (!header || header === '+') return sum;
    return sum + getSheinListingFieldColWidth(header);
  }, 0);
}

/** 每行最多展示的字段列数（固定规则，不随容器宽度变化） */
export function getSheinListingColumnsPerRow() {
  return SHEIN_LISTING_MAX_COLUMNS_PER_ROW;
}

export function chunkArrayItems(items, chunkSize) {
  if (!Array.isArray(items) || items.length === 0) return [];
  if (!chunkSize || chunkSize <= 0) return [items];
  const chunks = [];
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }
  return chunks;
}

export function buildFilteredGenericHeaders(allHeaders, plan) {
  if (Array.isArray(plan.dataColIndices) && plan.dataColIndices.length > 0) {
    const slice = plan.dataColIndices.map((index) => (allHeaders || [])[index + 1]).filter(Boolean);
    return ['+', ...slice, '操作'];
  }
  return buildFilteredTemuHeaders(allHeaders, plan.startDataCol, plan.colCount);
}

/** SHEIN 基本信息/商品属性：仅数据列表头，不含 + 与操作列 */
export function buildSheinFieldOnlyHeaders(allHeaders, plan) {
  if (Array.isArray(plan.dataColIndices) && plan.dataColIndices.length > 0) {
    return plan.dataColIndices
      .map((index) => (allHeaders || [])[index + 1])
      .filter((name) => name && name !== '+' && name !== '操作');
  }
  const dataHeaders = (allHeaders || []).slice(1, -1);
  return dataHeaders.slice(plan.startDataCol, plan.startDataCol + plan.colCount);
}
